/**
 * Betslip Service
 *
 * Handles all betslip-related API calls including:
 * - Image upload with OCR parsing
 * - Text paste parsing
 * - Manual entry
 * - Status updates
 * - Analytics
 */

import api from './api';

const betslipService = {
  /**
   * Fetch betslip image and return as blob URL
   * @param {number} betslipId - Betslip ID
   * @returns {Promise<string>} Blob URL for the image
   */
  fetchImage: async (betslipId) => {
    const response = await api.get(`/betslips/${betslipId}/image`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  /**
   * Fetch betslip thumbnail and return as blob URL
   * @param {number} betslipId - Betslip ID
   * @returns {Promise<string>} Blob URL for the thumbnail
   */
  fetchThumbnail: async (betslipId) => {
    const response = await api.get(`/betslips/${betslipId}/thumbnail`, {
      responseType: 'blob',
    });
    return URL.createObjectURL(response.data);
  },

  /**
   * Upload betslip image(s)
   * @param {File|File[]} imageFiles - Single image file or array of image files
   * @param {string} betDate - Bet date (YYYY-MM-DD)
   * @param {string} notes - Optional notes
   * @param {string} source - Source/handicapper name
   * @param {string} sourceUrl - URL to source
   * @returns {Promise} Betslip data
   */
  uploadBetslip: async (imageFiles, betDate, notes = '', source = '', sourceUrl = '') => {
    const formData = new FormData();
    // Backend expects 'images' (plural) as field name for List[UploadFile]
    const files = Array.isArray(imageFiles) ? imageFiles : [imageFiles];
    files.forEach(file => {
      formData.append('images', file);
    });
    if (betDate) formData.append('bet_date', betDate);
    if (notes) formData.append('notes', notes);
    if (source) formData.append('source', source);
    if (sourceUrl) formData.append('source_url', sourceUrl);

    // Don't set Content-Type header - browser sets it automatically with boundary
    const response = await api.post('/betslips/upload', formData);
    return response.data;
  },

  /**
   * Parse betslip from pasted text
   * @param {string} text - Raw betslip text
   * @param {string} betDate - Bet date (YYYY-MM-DD)
   * @param {string} source - Source/handicapper name
   * @param {string} sourceUrl - URL to source
   * @returns {Promise} Betslip data
   */
  parseTextBetslip: async (text, betDate = null, source = '', sourceUrl = '') => {
    const response = await api.post('/betslips/parse-text', {
      text,
      bet_date: betDate,
      source,
      source_url: sourceUrl,
    });
    return response.data;
  },

  /**
   * Create betslip manually
   * @param {object} betslipData - Betslip data
   * @returns {Promise} Created betslip
   */
  createBetslipManually: async (betslipData) => {
    const response = await api.post('/betslips/manual', betslipData);
    return response.data;
  },

  /**
   * Get user's betslips
   * @param {string} status - Filter by status (optional)
   * @param {number} limit - Max results
   * @returns {Promise} Array of betslips
   */
  getBetslips: async (status = null, limit = 50) => {
    const params = { limit };
    if (status) params.status_filter = status;  // Backend expects 'status_filter'

    const response = await api.get('/betslips/', { params });
    return response.data;
  },

  /**
   * Get single betslip by ID
   * @param {number} betslipId - Betslip ID
   * @returns {Promise} Betslip data
   */
  getBetslip: async (betslipId) => {
    const response = await api.get(`/betslips/${betslipId}`);
    return response.data;
  },

  /**
   * Update betslip status
   * @param {number} betslipId - Betslip ID
   * @param {string} status - New status (won, lost, pushed, cancelled)
   * @param {number} actualPayout - Actual payout amount (for won bets)
   * @returns {Promise} Updated betslip
   */
  updateStatus: async (betslipId, status, actualPayout = null) => {
    const data = { status };
    if (actualPayout !== null) data.actual_payout = actualPayout;

    const response = await api.patch(`/betslips/${betslipId}/status`, data);
    return response.data;
  },

  /**
   * Delete a betslip
   * @param {number} betslipId - Betslip ID
   * @returns {Promise}
   */
  deleteBetslip: async (betslipId) => {
    await api.delete(`/betslips/${betslipId}`);
  },

  /**
   * Update individual leg results for a betslip
   * @param {number} betslipId - Betslip ID
   * @param {Array} legUpdates - Array of {leg_id, result} objects
   * @param {number} actualPayout - Optional actual payout amount
   * @returns {Promise} Updated betslip
   */
  updateLegResults: async (betslipId, legUpdates, actualPayout = null) => {
    const data = {
      legs: legUpdates,
    };
    if (actualPayout !== null) {
      data.actual_payout = actualPayout;
    }

    const response = await api.patch(`/betslips/${betslipId}/legs`, data);
    return response.data;
  },

  /**
   * Auto-resolve pending betslips
   * @param {number} daysBack - Days to look back for pending bets
   * @returns {Promise} Resolution summary
   */
  resolvePendingBetslips: async (daysBack = 7) => {
    const response = await api.post('/betslips/resolve', null, {
      params: { days_back: daysBack },
    });
    return response.data;
  },

  /**
   * Get betting analytics
   * @param {number} daysBack - Days to look back (null for all time)
   * @param {string} sport - Filter by sport (optional)
   * @returns {Promise} Analytics data
   */
  getAnalytics: async (daysBack = null, sport = null) => {
    const params = {};
    if (daysBack) params.days_back = daysBack;
    if (sport) params.sport = sport;

    const response = await api.get('/betslips/analytics/summary', { params });
    return response.data;
  },

  /**
   * Get user betting stats summary
   * @returns {Promise} Stats data with win rate, ROI, profit
   */
  getUserStats: async () => {
    try {
      const response = await api.get('/betslips/analytics/summary');
      const analytics = response.data;

      // Transform analytics to stats format expected by the page
      return {
        betslips: {
          total: analytics.summary?.total_bets || 0,
          won: analytics.summary?.won || 0,
          lost: analytics.summary?.lost || 0,
          pending: analytics.summary?.pending || 0,
          total_staked: analytics.summary?.total_staked || 0,
          total_payout: analytics.summary?.total_payout || 0,
          net_profit: analytics.summary?.net_profit || 0
        }
      };
    } catch (error) {
      console.error('Error fetching user stats:', error);
      // Return empty stats on error
      return {
        betslips: {
          total: 0,
          won: 0,
          lost: 0,
          pending: 0,
          total_staked: 0,
          total_payout: 0,
          net_profit: 0
        }
      };
    }
  },

  // ============================================================
  // UPLOAD PREVIEW & CONFIRM (Review step before saving)
  // ============================================================

  /**
   * Preview betslip upload - runs OCR + matching but does NOT save to DB
   * @param {File[]} imageFiles - Image files to process
   * @param {string} betDate - Bet date (YYYY-MM-DD)
   * @returns {Promise} Preview data with enriched legs and confidence scores
   */
  previewBetslipUpload: async (imageFiles, betDate) => {
    const formData = new FormData();
    const files = Array.isArray(imageFiles) ? imageFiles : [imageFiles];
    files.forEach(file => {
      formData.append('images', file);
    });
    if (betDate) formData.append('bet_date', betDate);

    // Don't set Content-Type header - browser sets it automatically with boundary
    const response = await api.post('/betslips/upload-preview', formData);
    return response.data;
  },

  /**
   * Confirm and save a previously previewed betslip
   * @param {File[]} imageFiles - Same images from preview step
   * @param {object} betslipData - Confirmed betslip data (legs, sportsbook, etc.)
   * @returns {Promise} Created betslip
   */
  confirmBetslipUpload: async (imageFiles, betslipData) => {
    const formData = new FormData();
    const files = Array.isArray(imageFiles) ? imageFiles : [imageFiles];
    files.forEach(file => {
      formData.append('images', file);
    });
    formData.append('betslip_json', JSON.stringify(betslipData));

    // Don't set Content-Type header - browser sets it automatically with boundary
    const response = await api.post('/betslips/upload-confirm', formData);
    return response.data;
  },

  // ============================================================
  // SHARE LINK PARSING (FanDuel, BetMGM, DraftKings, etc.)
  // ============================================================

  /**
   * Parse a sportsbook share link
   * @param {string} shareUrl - Share URL from any supported sportsbook
   * @returns {Promise} Parsed share link data
   */
  parseShareLink: async (shareUrl) => {
    const response = await api.post('/betslips/parse-share-link', {
      share_url: shareUrl,
    });
    return response.data;
  },

  /**
   * Preview a share link with matched props
   * @param {string} shareUrl - Share URL
   * @returns {Promise} Preview with matched/unmatched legs
   */
  previewShareLink: async (shareUrl) => {
    const response = await api.post('/betslips/preview-share-link', {
      share_url: shareUrl,
    });
    return response.data;
  },

  /**
   * Create betslip from share link with user-provided leg details
   * @param {string} shareUrl - Original share URL
   * @param {Array} legs - Array of leg details
   * @param {object} options - Additional options (betDate, stake, source, notes)
   * @returns {Promise} Created betslip
   */
  createFromShareLink: async (shareUrl, legs, options = {}) => {
    const response = await api.post('/betslips/create-from-share-link', {
      share_url: shareUrl,
      legs,
      bet_date: options.betDate,
      stake: options.stake,
      source: options.source,
      notes: options.notes,
    });
    return response.data;
  },

  /**
   * Get today's props for manual matching
   * @param {string} sport - Filter by sport (optional)
   * @param {string} search - Search player name (optional)
   * @param {number} limit - Max results
   * @returns {Promise} Array of props
   */
  getTodaysProps: async (sport = null, search = null, limit = 50) => {
    const params = { limit };
    if (sport) params.sport = sport;
    if (search) params.search = search;

    const response = await api.get('/betslips/todays-props', { params });
    return response.data;
  },

  // ============================================================
  // BETSLIP ANALYSIS (uses existing ML predictions, no AI call)
  // ============================================================

  /**
   * Analyze a betslip against our ML predictions and streak data
   * @param {number} betslipId - Betslip ID to analyze
   * @returns {Promise} Analysis with confidence scores and recommendations
   */
  analyzeBetslip: async (betslipId) => {
    const response = await api.get(`/betslips/${betslipId}/analyze`);
    return response.data;
  },
};

export default betslipService;
